import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';
import RichTextEditor from './RichTextEditor';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const AdminFlashcards = () => {
  const [sets, setSets] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSet, setSelectedSet] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditSetDialog, setShowEditSetDialog] = useState(false);
  const [showCardDialog, setShowCardDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showPDFDialog, setShowPDFDialog] = useState(false);
  const [showSessionDialog, setShowSessionDialog] = useState(false);
  const [showGeneratedCardsDialog, setShowGeneratedCardsDialog] = useState(false);
  const { toast } = useToast();

  // Form states
  const [setTitle, setSetTitle] = useState('');
  const [setDescription, setSetDescription] = useState('');
  const [cardQuestion, setCardQuestion] = useState('');
  const [cardAnswer, setCardAnswer] = useState('');
  const [cardPoints, setCardPoints] = useState(10);
  const [editingCard, setEditingCard] = useState(null);
  const [cards, setCards] = useState([]);
  const [assignedUsers, setAssignedUsers] = useState([]);
  
  // PDF generation states
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingPDF, setUploadingPDF] = useState(false);
  const [generatedCards, setGeneratedCards] = useState([]);
  
  // Session generation states
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [generatingFromSession, setGeneratingFromSession] = useState(false);

  useEffect(() => {
    fetchSets();
    fetchUsers();
  }, []);

  const fetchSets = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await api.get('/admin/flashcards/sets', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSets(response.data.sets);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load flashcard sets",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await api.get('/admin/flashcards/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const fetchCards = async (setId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await api.get(`/admin/flashcards/sets/${setId}/cards`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCards(response.data.cards);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load cards",
        variant: "destructive"
      });
    }
  };

  const fetchAssignments = async (setId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await api.get(`/admin/flashcards/sets/${setId}/assignments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssignedUsers(response.data.user_ids);
    } catch (error) {
      console.error('Error loading assignments:', error);
    }
  };

  const handleCreateSet = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      await api.post('/admin/flashcards/sets', {
        title: setTitle,
        description: setDescription
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Success",
        description: "Flashcard set created successfully"
      });
      
      setShowCreateDialog(false);
      setSetTitle('');
      setSetDescription('');
      fetchSets();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create flashcard set",
        variant: "destructive"
      });
    }
  };

  const handleEditSet = () => {
    setSetTitle(selectedSet.title);
    setSetDescription(selectedSet.description || '');
    setShowEditSetDialog(true);
  };

  const handleUpdateSet = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      await api.put(`/admin/flashcards/sets/${selectedSet.set_id}`, {
        title: setTitle,
        description: setDescription
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Success",
        description: "Flashcard set updated successfully"
      });
      
      // Update local state
      setSelectedSet({
        ...selectedSet,
        title: setTitle,
        description: setDescription
      });
      
      setShowEditSetDialog(false);
      setSetTitle('');
      setSetDescription('');
      fetchSets();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update flashcard set",
        variant: "destructive"
      });
    }
  };

  const handleDeleteSet = async (setId) => {
    if (!window.confirm('Are you sure you want to delete this flashcard set? This will also delete all cards and assignments.')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await api.delete(`/admin/flashcards/sets/${setId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Success",
        description: "Flashcard set deleted successfully"
      });
      
      fetchSets();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete flashcard set",
        variant: "destructive"
      });
    }
  };

  const handleManageSet = async (set) => {
    setSelectedSet(set);
    await fetchCards(set.set_id);
    await fetchAssignments(set.set_id);
  };

  const handleAddCard = async () => {
    if (!cardQuestion.trim() || !cardAnswer.trim()) {
      toast({
        title: "Validation Error",
        description: "Question and answer are required",
        variant: "destructive"
      });
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const nextOrder = cards.length + 1;
      
      if (editingCard) {
        await api.put(`/admin/flashcards/cards/${editingCard.card_id}`, {
          question: cardQuestion,
          answer: cardAnswer,
          points: cardPoints
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast({
          title: "Success",
          description: "Card updated successfully"
        });
      } else {
        await api.post(`/admin/flashcards/sets/${selectedSet.set_id}/cards`, {
          question: cardQuestion,
          answer: cardAnswer,
          points: cardPoints,
          order: nextOrder
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast({
          title: "Success",
          description: "Card added successfully"
        });
      }
      
      setShowCardDialog(false);
      setCardQuestion('');
      setCardAnswer('');
      setCardPoints(10);
      setEditingCard(null);
      fetchCards(selectedSet.set_id);
      fetchSets();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save card",
        variant: "destructive"
      });
    }
  };

  const handleDeleteCard = async (cardId) => {
    if (!window.confirm('Are you sure you want to delete this card?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await api.delete(`/admin/flashcards/cards/${cardId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Success",
        description: "Card deleted successfully"
      });
      
      fetchCards(selectedSet.set_id);
      fetchSets();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete card",
        variant: "destructive"
      });
    }
  };

  const handleEditCard = (card) => {
    setEditingCard(card);
    setCardQuestion(card.question);
    setCardAnswer(card.answer);
    setCardPoints(card.points || 10);
    setShowCardDialog(true);
  };

  const handleAssignUsers = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      await api.post(`/admin/flashcards/sets/${selectedSet.set_id}/assign`, {
        user_ids: assignedUsers
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Success",
        description: "Users assigned successfully"
      });
      
      setShowAssignDialog(false);
      fetchSets();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to assign users",
        variant: "destructive"
      });
    }
  };

  const toggleUserAssignment = (userId) => {
    setAssignedUsers(prev => 
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // PDF Generation Handlers
  const handlePDFUpload = async () => {
    if (!selectedFile) {
      toast({
        title: "Error",
        description: "Please select a PDF file",
        variant: "destructive"
      });
      return;
    }

    setUploadingPDF(true);
    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.post('/admin/flashcards/generate-from-pdf', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setGeneratedCards(response.data.flashcards);
      setShowPDFDialog(false);
      setShowGeneratedCardsDialog(true);
      
      toast({
        title: "Success",
        description: response.data.message
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to process PDF",
        variant: "destructive"
      });
    } finally {
      setUploadingPDF(false);
      setSelectedFile(null);
    }
  };

  // Session Generation Handlers
  const fetchSessionsForGeneration = async () => {
    setLoadingSessions(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await api.get('/admin/flashcards/study-sessions', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(response.data.sessions);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load study sessions",
        variant: "destructive"
      });
    } finally {
      setLoadingSessions(false);
    }
  };

  const handleGenerateFromSession = async () => {
    if (!selectedSession) {
      toast({
        title: "Error",
        description: "Please select a study session",
        variant: "destructive"
      });
      return;
    }

    setGeneratingFromSession(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await api.post(
        `/admin/flashcards/generate-from-session?session_id=${selectedSession}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setGeneratedCards(response.data.flashcards);
      setShowSessionDialog(false);
      setShowGeneratedCardsDialog(true);
      
      toast({
        title: "Success",
        description: response.data.message
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to generate flashcards",
        variant: "destructive"
      });
    } finally {
      setGeneratingFromSession(false);
      setSelectedSession('');
    }
  };

  const handleSaveGeneratedCards = async () => {
    if (!setTitle.trim()) {
      toast({
        title: "Error",
        description: "Please enter a set title",
        variant: "destructive"
      });
      return;
    }

    if (generatedCards.length === 0) {
      toast({
        title: "Error",
        description: "No cards to save",
        variant: "destructive"
      });
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      
      // Create the set
      const setResponse = await api.post('/admin/flashcards/sets', {
        title: setTitle,
        description: setDescription
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const newSetId = setResponse.data.set_id;

      // Add all generated cards to the set
      for (let i = 0; i < generatedCards.length; i++) {
        await api.post(`/admin/flashcards/sets/${newSetId}/cards`, {
          question: generatedCards[i].question,
          answer: generatedCards[i].answer,
          points: generatedCards[i].points || 10,
          order: i
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      toast({
        title: "Success",
        description: `Flashcard set created with ${generatedCards.length} cards`
      });

      setShowGeneratedCardsDialog(false);
      setGeneratedCards([]);
      setSetTitle('');
      setSetDescription('');
      fetchSets();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save flashcard set",
        variant: "destructive"
      });
    }
  };

  const handleRemoveGeneratedCard = (index) => {
    setGeneratedCards(prev => prev.filter((_, i) => i !== index));
  };

  const handleEditGeneratedCard = (index, field, value) => {
    setGeneratedCards(prev => prev.map((card, i) => 
      i === index ? { ...card, [field]: value } : card
    ));
  };

  const handleSortByReference = async () => {
    if (!selectedSet || cards.length === 0) return;

    try {
      const token = localStorage.getItem('adminToken');
      
      // Call backend to sort cards
      await api.post(`/admin/flashcards/sets/${selectedSet.set_id}/sort-by-reference`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Success",
        description: "Cards sorted by biblical reference"
      });
      
      // Refresh cards
      await fetchCards(selectedSet.set_id);
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to sort cards",
        variant: "destructive"
      });
    }
  };

  const handleMoveCard = async (cardIndex, direction) => {
    const newIndex = direction === 'up' ? cardIndex - 1 : cardIndex + 1;
    
    if (newIndex < 0 || newIndex >= cards.length) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      const card1 = cards[cardIndex];
      const card2 = cards[newIndex];
      
      // Swap orders
      await api.put(`/admin/flashcards/cards/${card1.card_id}`, {
        order: card2.order
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await api.put(`/admin/flashcards/cards/${card2.card_id}`, {
        order: card1.order
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh cards to show new order
      await fetchCards(selectedSet.set_id);
      
      toast({
        title: "Success",
        description: "Card order updated"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to reorder card",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading flashcard sets...</p>
      </div>
    );
  }

  if (selectedSet) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold text-gray-800">{selectedSet.title}</h2>
              <Button 
                onClick={handleEditSet}
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
              >
                ✏️ Edit
              </Button>
            </div>
            <p className="text-gray-600">{selectedSet.description}</p>
          </div>
          <Button onClick={() => setSelectedSet(null)} variant="outline">
            ← Back to Sets
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Cards Management */}
          <Card>
            <CardHeader>
              <CardTitle>Flashcards ({cards.length})</CardTitle>
              <CardDescription>Manage questions and answers</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 mb-4">
                <Button 
                  onClick={() => {
                    setEditingCard(null);
                    setCardQuestion('');
                    setCardAnswer('');
                    setCardPoints(10);
                    setShowCardDialog(true);
                  }}
                  className="w-full"
                >
                  + Add New Card
                </Button>
                <Button 
                  onClick={handleSortByReference}
                  variant="outline"
                  className="w-full"
                >
                  📖 Sort by Biblical Reference
                </Button>
              </div>

              <div className="space-y-3 max-h-96 overflow-y-auto">
                {cards.map((card, index) => (
                  <div key={card.card_id} className="p-4 border rounded-lg bg-gray-50">
                    <div className="flex items-start justify-between mb-2">
                      <span className="text-xs text-gray-500">Card {index + 1}</span>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleMoveCard(index, 'up')}
                          disabled={index === 0}
                          title="Move up"
                        >
                          ↑
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleMoveCard(index, 'down')}
                          disabled={index === cards.length - 1}
                          title="Move down"
                        >
                          ↓
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleEditCard(card)}
                        >
                          Edit
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => handleDeleteCard(card.card_id)}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                    <div className="font-semibold text-sm mb-1">
                      <span className="text-gray-600">Q: </span>
                      <span dangerouslySetInnerHTML={{ __html: card.question }} />
                    </div>
                    <div className="text-sm text-gray-700">
                      <span className="text-gray-600">A: </span>
                      <span dangerouslySetInnerHTML={{ __html: card.answer }} />
                    </div>
                    <div className="text-xs text-green-600 font-semibold mt-2">
                      ⭐ {card.points || 10} points
                    </div>
                  </div>
                ))}
                {cards.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No cards yet. Add your first card!</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* User Assignments */}
          <Card>
            <CardHeader>
              <CardTitle>Assigned Users ({selectedSet.assigned_users || 0})</CardTitle>
              <CardDescription>Control who can access this set</CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={() => setShowAssignDialog(true)}
                className="w-full"
              >
                Manage Assignments
              </Button>

              <div className="mt-4">
                <p className="text-sm text-gray-600 mb-2">Currently assigned to:</p>
                <div className="space-y-2">
                  {users.filter(u => assignedUsers.includes(u.user_id)).map(user => (
                    <div key={user.user_id} className="flex items-center gap-2 p-2 bg-blue-50 rounded">
                      <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold">{user.name}</p>
                        <p className="text-xs text-gray-600">{user.email}</p>
                      </div>
                    </div>
                  ))}
                  {assignedUsers.length === 0 && (
                    <p className="text-sm text-gray-500 italic">No users assigned yet</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Add/Edit Card Dialog */}
        <Dialog open={showCardDialog} onOpenChange={setShowCardDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingCard ? 'Edit Card' : 'Add New Card'}</DialogTitle>
              <DialogDescription>
                Create a question and answer pair. Use the formatting buttons for bold, italic, and underline.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="question">Question</Label>
                <RichTextEditor
                  value={cardQuestion}
                  onChange={setCardQuestion}
                  placeholder="Enter the question"
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="answer">Answer</Label>
                <RichTextEditor
                  value={cardAnswer}
                  onChange={setCardAnswer}
                  placeholder="Enter the answer"
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="points">Points</Label>
                <Input
                  id="points"
                  type="number"
                  value={cardPoints}
                  onChange={(e) => setCardPoints(parseInt(e.target.value) || 10)}
                  placeholder="Enter points (default: 10)"
                  min="1"
                  max="100"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowCardDialog(false);
                setEditingCard(null);
                setCardQuestion('');
                setCardAnswer('');
                setCardPoints(10);
              }}>
                Cancel
              </Button>
              <Button onClick={handleAddCard}>
                {editingCard ? 'Update Card' : 'Add Card'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assign Users Dialog */}
        <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Assign Users</DialogTitle>
              <DialogDescription>
                Select which users can access this flashcard set
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {users.map(user => (
                <div key={user.user_id} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={assignedUsers.includes(user.user_id)}
                    onChange={() => toggleUserAssignment(user.user_id)}
                    className="w-4 h-4"
                  />
                  <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                  <div className="flex-1">
                    <p className="font-semibold">{user.name}</p>
                    <p className="text-sm text-gray-600">{user.email}</p>
                  </div>
                </div>
              ))}
              {users.length === 0 && (
                <p className="text-center text-gray-500 py-8">No registered users yet</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleAssignUsers}>
                Save Assignments
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Set Dialog */}
        <Dialog open={showEditSetDialog} onOpenChange={setShowEditSetDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Flashcard Set</DialogTitle>
              <DialogDescription>
                Update the title and description
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-title">Title</Label>
                <Input
                  id="edit-title"
                  value={setTitle}
                  onChange={(e) => setSetTitle(e.target.value)}
                  placeholder="e.g., Multiplication Tables, Bible Verses"
                />
              </div>
              <div>
                <Label htmlFor="edit-description">Description (Optional)</Label>
                <Textarea
                  id="edit-description"
                  value={setDescription}
                  onChange={(e) => setSetDescription(e.target.value)}
                  placeholder="Brief description of this flashcard set"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowEditSetDialog(false);
                setSetTitle('');
                setSetDescription('');
              }}>
                Cancel
              </Button>
              <Button onClick={handleUpdateSet} disabled={!setTitle.trim()}>
                Update Set
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Flashcard Sets Management</h2>
          <p className="text-gray-600">Create and manage personalized flashcard sets</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          + Create New Set
        </Button>
      </div>

      {sets.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-600 text-lg mb-4">No flashcard sets created yet.</p>
            <Button onClick={() => setShowCreateDialog(true)}>
              Create Your First Set
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sets.map((set) => (
            <Card key={set.set_id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle>{set.title}</CardTitle>
                {set.description && (
                  <CardDescription>{set.description}</CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-2 mb-4">
                  <p className="text-sm text-gray-600">
                    📝 {set.card_count} {set.card_count === 1 ? 'card' : 'cards'}
                  </p>
                  <p className="text-sm text-gray-600">
                    👥 {set.assigned_users} {set.assigned_users === 1 ? 'user' : 'users'} assigned
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => handleManageSet(set)}
                    className="flex-1"
                  >
                    Manage
                  </Button>
                  <Button 
                    onClick={() => handleDeleteSet(set.set_id)}
                    variant="destructive"
                  >
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Set Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Flashcard Set</DialogTitle>
            <DialogDescription>
              Add a title and description for your new flashcard set, or generate cards automatically
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={setTitle}
                onChange={(e) => setSetTitle(e.target.value)}
                placeholder="e.g., Multiplication Tables, Bible Verses"
              />
            </div>
            <div>
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                value={setDescription}
                onChange={(e) => setSetDescription(e.target.value)}
                placeholder="Brief description of this flashcard set"
                rows={3}
              />
            </div>
            
            {/* Automated Generation Buttons */}
            <div className="border-t pt-4 space-y-3">
              <p className="text-sm font-semibold text-gray-700">Automated Card Generation:</p>
              <div className="grid grid-cols-2 gap-3">
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => {
                    setShowCreateDialog(false);
                    setShowPDFDialog(true);
                  }}
                >
                  📄 Generate from PDF
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => {
                    setShowCreateDialog(false);
                    fetchSessionsForGeneration();
                    setShowSessionDialog(true);
                  }}
                >
                  📚 Generate from Session
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateSet} disabled={!setTitle.trim()}>
              Create Set
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Upload Dialog */}
      <Dialog open={showPDFDialog} onOpenChange={setShowPDFDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Flashcards from PDF</DialogTitle>
            <DialogDescription>
              Upload a PDF containing questions and answers. The system will automatically extract Q&A pairs.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="pdf-file">Select PDF File</Label>
              <Input
                id="pdf-file"
                type="file"
                accept=".pdf"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                className="cursor-pointer"
              />
              {selectedFile && (
                <p className="text-sm text-gray-600 mt-2">
                  Selected: {selectedFile.name}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowPDFDialog(false);
              setSelectedFile(null);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handlePDFUpload} 
              disabled={!selectedFile || uploadingPDF}
            >
              {uploadingPDF ? 'Processing...' : 'Generate Flashcards'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Session Selection Dialog */}
      <Dialog open={showSessionDialog} onOpenChange={setShowSessionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Flashcards from Session</DialogTitle>
            <DialogDescription>
              Select a study session. Flashcards will be generated from all lessons with "Review" in their title.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="session-select">Select Study Session</Label>
              {loadingSessions ? (
                <p className="text-sm text-gray-600 py-2">Loading sessions...</p>
              ) : (
                <select
                  id="session-select"
                  value={selectedSession}
                  onChange={(e) => setSelectedSession(e.target.value)}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="">-- Select a session --</option>
                  {sessions.map(session => (
                    <option key={session.session_id} value={session.session_id}>
                      {session.title} ({session.review_lesson_count} review lessons)
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowSessionDialog(false);
              setSelectedSession('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleGenerateFromSession} 
              disabled={!selectedSession || generatingFromSession}
            >
              {generatingFromSession ? 'Generating...' : 'Generate Flashcards'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generated Cards Preview Dialog */}
      <Dialog open={showGeneratedCardsDialog} onOpenChange={setShowGeneratedCardsDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review Generated Flashcards</DialogTitle>
            <DialogDescription>
              Review and edit the generated flashcards before saving. You can remove unwanted cards or edit their content.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="generated-title">Set Title</Label>
              <Input
                id="generated-title"
                value={setTitle}
                onChange={(e) => setSetTitle(e.target.value)}
                placeholder="Enter a title for this flashcard set"
              />
            </div>
            <div>
              <Label htmlFor="generated-description">Description (Optional)</Label>
              <Textarea
                id="generated-description"
                value={setDescription}
                onChange={(e) => setSetDescription(e.target.value)}
                placeholder="Brief description"
                rows={2}
              />
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-semibold text-gray-700 mb-3">
                Generated Cards ({generatedCards.length})
              </p>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {generatedCards.map((card, index) => (
                  <div key={index} className="border rounded p-3 bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-xs text-gray-500">Card {index + 1}</span>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleRemoveGeneratedCard(index)}
                      >
                        Remove
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <div>
                        <Label className="text-xs">Question</Label>
                        <Textarea
                          value={card.question}
                          onChange={(e) => handleEditGeneratedCard(index, 'question', e.target.value)}
                          rows={2}
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Answer</Label>
                        <Textarea
                          value={card.answer}
                          onChange={(e) => handleEditGeneratedCard(index, 'answer', e.target.value)}
                          rows={2}
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Points</Label>
                        <Input
                          type="number"
                          value={card.points || 10}
                          onChange={(e) => handleEditGeneratedCard(index, 'points', parseInt(e.target.value) || 10)}
                          min="1"
                          max="100"
                          className="text-sm"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowGeneratedCardsDialog(false);
              setGeneratedCards([]);
              setSetTitle('');
              setSetDescription('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveGeneratedCards}
              disabled={!setTitle.trim() || generatedCards.length === 0}
            >
              Save Flashcard Set ({generatedCards.length} cards)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminFlashcards;
